import { useRef, useEffect } from "react";
import {
  MESSAGE_TYPES,
  Credentials,
  ProvideCredentialsMessage,
  IncomingMessage,
} from "../types/messages";

/**
 * Hook to handle iframe credential communication.
 * Manages secure credential exchange between the host and iframe application.
 */
export const useIframeCredentials = (
  credentials: Credentials | null,
  targetOrigin: string,
  bridgePort?: MessagePort | null,
): { iframeRef: React.RefObject<HTMLIFrameElement> | null } => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const bridgePortSentRef = useRef(false);

  useEffect(() => {
    // Set up listener as soon as we have credentials, don't wait for iframe ref
    if (!credentials) {
      return;
    }

    const sendCredentials = () => {
      if (!iframeRef.current?.contentWindow) {
        return;
      }

      try {
        const message: ProvideCredentialsMessage = {
          type: MESSAGE_TYPES.PROVIDE_CREDENTIALS,
          credentials: {
            token: credentials.token,
            project: credentials.project,
            baseUrl: credentials.baseUrl,
          },
        };

        const transferables: Transferable[] = [];
        if (bridgePort && !bridgePortSentRef.current) {
          transferables.push(bridgePort);
          bridgePortSentRef.current = true;
        }

        iframeRef.current.contentWindow.postMessage(
          message,
          targetOrigin,
          transferables,
        );
      } catch (error) {
        console.error("Failed to send credentials to iframe:", error);
      }
    };

    const handleMessage = (event: MessageEvent) => {
      // Filter out noise messages from setImmediate and other sources
      if (
        typeof event.data === "string" &&
        event.data.startsWith("setImmediate")
      ) {
        return;
      }

      // Only process messages from our inner iframe, not from parent window
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }

      // Validate origin for security - must match our expected targetOrigin
      if (event.origin !== targetOrigin) {
        console.warn(
          `Ignoring message from untrusted origin: ${event.origin}, expected: ${targetOrigin}`,
        );
        return;
      }

      // Type guard for incoming messages
      const message = event.data as IncomingMessage;

      // Handle REQUEST_CREDENTIALS message from iframe
      if (message?.type === MESSAGE_TYPES.REQUEST_CREDENTIALS) {
        sendCredentials();
      }
    };

    // Listen for messages from iframe
    window.addEventListener("message", handleMessage);

    // Cleanup
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [credentials, targetOrigin, bridgePort]);

  if (!credentials) {
    // we will wait for credentials to be available before
    // we are ready to return an iframeRef
    return { iframeRef: null };
  }

  return { iframeRef };
};
